import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Bell, BellRing, CheckCheck, Truck, Package, AlertTriangle, X, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

const iconMap = {
  truck: Truck,
  check: CheckCircle2,
  alert: AlertTriangle,
  package: Package,
  bell: Bell,
  info: Info,
};

// Base64URL → Uint8Array (required for VAPID public key)
const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const NotificationBell = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotifs = useCallback(async () => {
    if (!token) return;
    try {
      const [lst, cnt] = await Promise.all([
        axios.get(`${API}/notifications`, { headers }),
        axios.get(`${API}/notifications/unread-count`, { headers }),
      ]);
      const prevUnread = unread;
      setNotifs(lst.data);
      setUnread(cnt.data.count);
      // Sound ping if new notification arrived while polling
      if (cnt.data.count > prevUnread && prevUnread > 0) {
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain); gain.connect(ctx.destination);
          osc.frequency.value = 880; gain.gain.value = 0.1;
          osc.start(); osc.stop(ctx.currentTime + 0.15);
        } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }, [token, unread]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, 20000); // poll every 20s
    return () => clearInterval(id);
  }, [fetchNotifs]);

  // Check current push subscription state
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription();
      setPushEnabled(!!sub);
    }).catch(() => {});
  }, []);

  const enablePush = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      toast.error("Votre navigateur ne supporte pas les notifications push");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notifications refusées");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await axios.get(`${API}/notifications/vapid-public-key`);
      const appServerKey = urlBase64ToUint8Array(keyRes.data.key);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey,
      });
      const raw = sub.toJSON();
      await axios.post(`${API}/notifications/subscribe`, {
        endpoint: raw.endpoint,
        keys: raw.keys,
      }, { headers });
      setPushEnabled(true);
      toast.success("Notifications push activées !");
    } catch (e) {
      toast.error("Impossible d'activer les notifications");
    }
  };

  const sendTest = async () => {
    try {
      await axios.post(`${API}/notifications/test`, {}, { headers });
      toast.success("Notification de test envoyée");
      setTimeout(fetchNotifs, 400);
    } catch {
      toast.error("Erreur");
    }
  };

  const handleClick = async (n) => {
    try { await axios.post(`${API}/notifications/${n.id}/read`, {}, { headers }); } catch {}
    fetchNotifs();
    setOpen(false);
    if (n.lien) navigate(n.lien);
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, { headers });
      fetchNotifs();
    } catch {}
  };

  const deleteNotif = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/${id}`, { headers });
      fetchNotifs();
    } catch {}
  };

  const formatTime = (iso) => {
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "À l'instant";
      if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
      return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  if (!user || (user.role || "admin") !== "admin") return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notif-bell-btn"
        aria-label="Notifications"
      >
        {unread > 0 ? <BellRing className="w-5 h-5 text-indigo-600" /> : <Bell className="w-5 h-5 text-slate-500" />}
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-testid="notif-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800 text-sm">Notifications</h3>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button onClick={markAllRead} className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 px-2 py-1" data-testid="notif-mark-all-btn">
                    <CheckCheck className="w-3.5 h-3.5" />
                    Tout lu
                  </button>
                )}
              </div>
            </div>

            {/* Push toggle */}
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">
                Push téléphone : <span className={pushEnabled ? "text-emerald-600 font-medium" : "text-slate-400"}>{pushEnabled ? "Activées" : "Désactivées"}</span>
              </span>
              {!pushEnabled ? (
                <button onClick={enablePush} className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="notif-enable-push-btn">
                  Activer
                </button>
              ) : (
                <button onClick={sendTest} className="text-indigo-600 hover:text-indigo-700 font-medium" data-testid="notif-test-btn">
                  Tester
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto" data-testid="notif-list">
              {notifs.length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-sm">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Aucune notification</p>
                </div>
              ) : (
                notifs.map((n) => {
                  const Icon = iconMap[n.icon] || Bell;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 flex items-start gap-3 group ${!n.lue ? "bg-indigo-50/40" : ""}`}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        n.type === "tournee_terminee" ? "bg-emerald-100 text-emerald-600" :
                        n.type === "livraison_livree" ? "bg-indigo-100 text-indigo-600" :
                        n.type === "stock_bas" ? "bg-amber-100 text-amber-600" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${!n.lue ? "font-semibold text-slate-800" : "text-slate-600"}`}>{n.titre}</p>
                          {!n.lue && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                      <button
                        onClick={(e) => deleteNotif(n.id, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-500 transition-opacity"
                        aria-label="Supprimer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
